import type { EChartsOption } from "echarts";
import mustache from "mustache";
import { FincaClimaDataHorarioResponse } from "../../../modulos/clima/response/FincaClimaDataHorarioResponse";
import {
  etiquetaDiaCentrada,
  indicesInicioDia,
  indicesPorHora,
  itemsDeHoy,
  itemsDesdeHoy,
} from "../../../utils/climaUtils";
import { formatDiaMesDiaSemanaHora, formatHora } from "../../../utils/fechaUtils";
import { ClimaChartBase, ClimaChartModo } from "./ClimaChartBase";
import template from "./climaEvapotranspiracionHumedadTemplate.html";

mustache.parse(template);

/** Horas del día en que se marcan divisor + etiqueta en modo "dia". */
const HORAS_DIVISOR_DIA = [6, 12, 18];

/** Gráfico "Evapotranspiración vs Humedad Relativa": horas de hoy (Current) o
 *  desde hoy en adelante (Forecast, varios días) — siempre por hora, sin
 *  agregar por día. */
export class ClimaEvapotranspiracionHumedadChartRender extends ClimaChartBase<FincaClimaDataHorarioResponse> {
  constructor(private readonly _modo: ClimaChartModo = "dia") {
    super();
  }

  render(): string {
    return mustache.render(template, { chartId: this.chartId });
  }

  protected filterItems(
    items: FincaClimaDataHorarioResponse[],
  ): FincaClimaDataHorarioResponse[] {
    return this._modo === "dia" ? itemsDeHoy(items) : itemsDesdeHoy(items);
  }

  protected getOption(items: FincaClimaDataHorarioResponse[]): EChartsOption {
    const formatEtiqueta =
      this._modo === "dia" ? formatHora : formatDiaMesDiaSemanaHora;

    // "dia": divisor + label solo a las 06/12/18. "rango": divisor al inicio
    // de cada día, label centrada bajo su bloque de horas.
    const indicesDivisor =
      this._modo === "dia"
        ? indicesPorHora(items, HORAS_DIVISOR_DIA)
        : indicesInicioDia(items);
    const etiquetaEnIndice = (index: number): string =>
      this._modo === "dia"
        ? indicesDivisor.includes(index)
          ? formatHora(items[index].fecha)
          : ""
        : etiquetaDiaCentrada(items, index);

    return {
      tooltip: { trigger: "axis" },
      legend: { bottom: 0 },
      grid: { left: 48, right: 48, top: 24, bottom: 40 },
      xAxis: {
        type: "category",
        data: items.map((item) => formatEtiqueta(item.fecha)),
        axisLabel: {
          interval: 0,
          formatter: (_value: string, index: number) =>
            etiquetaEnIndice(index),
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Humedad Relativa",
          min: 0,
          max: 100,
          axisLabel: { formatter: "{value} %" },
        },
        {
          type: "value",
          name: "Evapotranspiración",
          min: 0,
          max: 1,
          axisLabel: { formatter: "{value} mm" },
        },
      ],
      series: [
        {
          name: "Humedad Relativa",
          type: "line",
          smooth: true,
          data: items.map((item) => item.relativehumidity),
          color: "#378add", // --blue-400
          markLine: {
            symbol: "none",
            label: { show: false },
            lineStyle: { type: "dashed", color: "#94a3b8" },
            data: indicesDivisor.map((index) => ({ xAxis: index })),
          },
        },
        {
          name: "Evapotranspiración",
          type: "bar",
          yAxisIndex: 1,
          data: items.map((item) => item.evapotranspiration),
          color: "#28f9f2", // cian, igual paleta que rainspot
        },
      ],
    };
  }
}
